/**
 * useLogin Composable
 * 
 * A composable that handles user authentication functionality including login and logout.
 * It manages the login form state, handles form submission, and provides error handling.
 * 
 * Features:
 * - Form state management (email, password)
 * - Login form submission with validation
 * - Error handling and loading states
 * - Logout functionality
 * - Automatic redirection after successful login
 * 
 * @returns {Object} An object containing form state, loading state, error state, and auth methods
 */

import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/ui/useToast'

export function useLogin() {
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const { error: toastError } = useToast()

  const email = ref('')
  const password = ref('')
  const loading = ref(false)
  const error = ref('')

  const handleLogin = async () => {
    try {
      loading.value = true
      error.value = ''

      // Validate input
      if (!email.value || !password.value) {
        throw new Error('Please enter both email and password')
      }

      // Call the actual login API
      await authStore.login({
        email: email.value,
        password: password.value
      })
      
      // Redirect to the original destination or home
      const redirectPath = route.query.redirect as string || '/'
      await router.push(redirectPath)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during login'
      error.value = errorMessage
      toastError(errorMessage, 6000)
    } finally {
      loading.value = false
    }
  }

  const handleLogout = async () => {
    try {
      await authStore.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return {
    email,
    password,
    loading,
    error,
    handleLogin,
    handleLogout
  }
} 